import { ListingResponse } from '~/modules/services/listing_service';
import { getPluginMembers, PluginMember } from '~/modules/services/pluginsService';
import { getListingId } from '~/modules/utilities/listing_utils';

export interface PackageMembersHost {
    members: PluginMember[];
    isLoadingMembers: boolean;
}

// Shared by skillFoundIn and pluginSpecs: both resolve a set of member listing ids off the current
// listing, fetch them, and re-fetch on in-place listing swaps (same-tab navigation, no remount).
export class PackageMembersFetcher {
    private lastFetchedListingId: string | null = null;
    private lastMemberSource: 'lookup' | 'provided' | null = null;
    private lastLookupMemberIdsSignature: string | null = null;
    private readonly host: PackageMembersHost;
    private readonly getMemberIds: (listing: ListingResponse) => string[];
    private requestVersion = 0;

    constructor(host: PackageMembersHost, getMemberIds: (listing: ListingResponse) => string[]) {
        this.host = host;
        this.getMemberIds = getMemberIds;
    }

    onListingChange(listing: ListingResponse): void {
        const listingId = getListingId(listing);
        const memberIds = this.getMemberIds(listing);
        const memberIdsSignature = JSON.stringify(memberIds);
        if (
            listingId === this.lastFetchedListingId &&
            this.lastMemberSource === 'lookup' &&
            memberIdsSignature === this.lastLookupMemberIdsSignature
        ) {
            return;
        }
        this.lastFetchedListingId = listingId;
        this.lastMemberSource = 'lookup';
        this.lastLookupMemberIdsSignature = memberIdsSignature;
        this.fetchMembers(memberIds).catch(() => undefined);
    }

    /**
     * Replaces API-resolved members with card data embedded in the current listing and invalidates
     * any outstanding lookup so a late response cannot overwrite the provided summaries.
     */
    useProvidedMembers(listing: ListingResponse, members: PluginMember[]): void {
        this.requestVersion += 1;
        this.lastFetchedListingId = getListingId(listing);
        this.lastMemberSource = 'provided';
        this.host.members = members;
        this.host.isLoadingMembers = false;
    }

    disconnect(): void {
        this.requestVersion += 1;
    }

    private async fetchMembers(ids: string[]): Promise<void> {
        const requestVersion = ++this.requestVersion;
        this.host.members = [];
        this.host.isLoadingMembers = false;
        if (ids.length === 0) {
            return;
        }
        this.host.isLoadingMembers = true;
        try {
            const members = await getPluginMembers(ids);
            if (requestVersion === this.requestVersion) {
                this.host.members = members;
            }
        } catch {
            // Member recommendations are supplemental. A failed lookup leaves an empty section;
            // callers must not get an unhandled rejection or retain members from the old listing.
            if (requestVersion === this.requestVersion) {
                this.host.members = [];
            }
        } finally {
            if (requestVersion === this.requestVersion) {
                this.host.isLoadingMembers = false;
            }
        }
    }
}
